import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { addMinutes, format, parse } from "date-fns";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { QRCodeCanvas } from "qrcode.react";

// --- Typdefinitionen ---
type Drink = {
  type: string;
  volume: number | "";
  alcohol: number;
  time: string;
};

type ChartPoint = {
  time: string;
  promille: number;
  numericTime: number;
};

type View = "calculator" | "impressum" | "privacy" | "faq" | "blog";

// --- Konstanten & Hilfsfunktionen ---
const drinkOptions: { type: string; alcohol: number }[] = [
  { type: "Bier", alcohol: 5 },
  { type: "Wein", alcohol: 12 },
  { type: "Sekt", alcohol: 11 },
  { type: "Likör", alcohol: 30 },
  { type: "Schnaps", alcohol: 40 },
  { type: "Longdrink", alcohol: 10 },
  { type: "Cocktail", alcohol: 15 },
  { type: "Individuell", alcohol: 0 }, // NEU: Option für individuelle Eingabe
];

const getCurrentTimeRoundedTo30Min = (): string => {
  const now = new Date();
  now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);
  return format(now, "HH:mm");
};

const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let h = 0; h < 24; h++) {
    options.push(`${String(h).padStart(2, "0")}:00`);
    options.push(`${String(h).padStart(2, "0")}:30`);
  }
  return options;
};

// --- Sub-Komponenten ---

const Logo = () => (
  <Link to="/" className="flex items-center space-x-2">
    <img src="/logo.png" alt="CalcAlc Logo" className="h-16 w-auto" />
  </Link>
);

// NEU: Header Komponente, die Logo und Navigation kombiniert
const Header = () => {
  const location = useLocation();
  const isActive = (path: string) =>
    location.pathname === path
      ? "text-purple-700 font-semibold"
      : "text-gray-500 hover:text-purple-600";

  return (
    <header className="flex items-center justify-between mb-8 w-full">
      <Logo />
      <nav className="flex items-center space-x-6">
        <Link
          to="/"
          className={`text-lg transition-colors duration-200 ${isActive("/")}`}
        >
          Rechner
        </Link>
        <Link
          to="/blog"
          className={`text-lg transition-colors duration-200 ${isActive(
            "/blog"
          )}`}
        >
          Blog
        </Link>
      </nav>
    </header>
  );
};

// Komponente für das Impressum
const Impressum = ({ setView }: { setView: (view: View) => void }) => (
  <div className="p-4 md:p-6 bg-white rounded-lg shadow-md animate-fade-in">
    <title>Impressum - CalcAlc</title>
    <meta
      name="description"
      content="Rechtliche Hinweise und Kontaktdaten für CalcAlc. Angaben gemäß § 5 TMG und verantwortlich für den Inhalt."
    />
    <link rel="canonical" href="https://calcalc.de/impressum" />
    {/* ... Inhalt ... */}
    <h1 className="text-2xl font-bold mb-4 text-gray-800">Impressum</h1>
    <div className="space-y-4 text-gray-700">
      <p>
        <strong>Angaben gemäß § 5 TMG:</strong>
      </p>
      <p>cengalo GmbH</p>
      <p>
        <strong>Kontakt:</strong>
        <br />
        Sitz der Gesellschaft:
        <br />
        Hohe Str. 23 / 1
        <br />
        72793 Pfullingen
        <br />
        <br />
        E-Mail: service@cengalo.com
        <br />
        <br />
        Handelsregister:
        <br />
        Eingetragen im Handelsregister des Amtsgerichts Stuttgart
        <br />
        Handelsregisternummer: HRB 107184
        <br />
        <br />
        Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG:
        <br />
        DE360817907
      </p>
      <p>
        <strong>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</strong>
        <br />
        cengalo GmbH
      </p>
      <h3 className="text-lg font-semibold pt-4">
        Haftungsausschluss (Disclaimer)
      </h3>
      <p>
        Die Inhalte dieses Promillerechners wurden mit größter Sorgfalt
        erstellt. Für die Richtigkeit, Vollständigkeit und Aktualität der
        Inhalte können wir jedoch keine Gewähr übernehmen. Die berechneten Werte
        dienen ausschließlich der Orientierung und sind rechtlich nicht bindend.
      </p>
    </div>
    <Link
      to="/"
      className="mt-6 inline-block px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
    >
      ← Zurück zum Rechner
    </Link>
  </div>
);

// Komponente für die Datenschutzerklärung
const PrivacyPolicy = ({ setView }: { setView: (view: View) => void }) => (
  <div className="p-4 md:p-6 bg-white rounded-lg shadow-md animate-fade-in">
    <title>Datenschutzerklärung - CalcAlc</title>
    <meta
      name="description"
      content="Erfahren Sie, wie CalcAlc Ihre Daten schützt. Wir verarbeiten alle Eingaben nur im Browser und speichern keine persönlichen Informationen."
    />
    <link rel="canonical" href="https://calcalc.de/datenschutz" />
    {/* ... Inhalt ... */}
    <h1 className="text-2xl font-bold mb-4 text-gray-800">
      Datenschutzerklärung
    </h1>
    <div className="space-y-4 text-gray-700">
      <p>
        Verantwortlicher im Sinne der Datenschutzgesetze, insbesondere der
        EU-Datenschutzgrundverordnung (DSGVO), ist:
        <br />
        eco4it GmbH (Kontaktdaten siehe Impressum)
      </p>
      <h3 className="text-lg font-semibold pt-4">Ihre Betroffenenrechte</h3>
      <p>
        Unter den angegebenen Kontaktdaten können Sie jederzeit folgende Rechte
        ausüben: Auskunft über Ihre bei uns gespeicherten Daten und deren
        Verarbeitung, Berichtigung unrichtiger personenbezogener Daten, Löschung
        Ihrer bei uns gespeicherten Daten.
      </p>
      <h3 className="text-lg font-semibold pt-4">
        Art und Zweck der Verarbeitung
      </h3>
      <p>
        Diese Website ist ein reiner Client-Side-Rechner. Alle von Ihnen
        eingegebenen Daten (Gewicht, Geschlecht, Getränke) werden ausschließlich
        in Ihrem Browser verarbeitet und zu keinem Zeitpunkt an einen Server
        gesendet oder gespeichert. Sobald Sie die Seite schließen oder neu
        laden, werden die Daten verworfen. Wir verwenden keine Cookies und
        führen kein Tracking durch.
      </p>
    </div>
    <Link
      to="/"
      className="mt-6 inline-block px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
    >
      ← Zurück zum Rechner
    </Link>
  </div>
);

// Komponente für die FAQ
const FAQ = ({ setView }: { setView: (view: View) => void }) => (
  <div className="p-4 md:p-6 bg-white rounded-lg shadow-md animate-fade-in">
    <title>FAQ - Häufig gestellte Fragen - CalcAlc</title>
    <meta
      name="description"
      content="Antworten auf die häufigsten Fragen zu unserem Promillerechner: Wie funktioniert die Widmark-Formel? Wie wird der Alkoholabbau berechnet?"
    />
    <link rel="canonical" href="https://calcalc.de/faq" />
    <script type="application/ld+json">
      {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Nach welcher Formel wird der Promillewert berechnet?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Unsere Berechnung basiert auf der anerkannten Widmark-Formel. Sie ist die gängigste Methode zur Schätzung der Blutalkoholkonzentration (BAK)."
                }
              },
              {
                "@type": "Question",
                "name": "Wie wird der Alkoholabbau im Körper simuliert?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Der Rechner simuliert sowohl die Aufnahme (Resorption) als auch den Abbau des Alkohols. Wir rechnen mit einem konstanten stündlichen Abbauwert von 0,15 Promille."
                }
              }
            ]
          }
        `}
    </script>
    {/* ... Inhalt ... */}
    <h1 className="text-2xl font-bold mb-4 text-gray-800">
      FAQ - Häufig gestellte Fragen
    </h1>
    <div className="space-y-6 text-gray-700">
      <div>
        <h3 className="text-lg font-semibold">
          Nach welcher Formel wird der Promillewert berechnet?
        </h3>
        <p className="mt-2">
          Unsere Berechnung basiert auf der anerkannten{" "}
          <strong>Widmark-Formel</strong>. Sie ist die gängigste Methode zur
          Schätzung der Blutalkoholkonzentration (BAK). Die Grundformel lautet:
        </p>
        <p className="mt-2 p-3 bg-gray-100 rounded-md text-center font-mono">
          ‰ = (Alkohol in Gramm) / (Körpergewicht in kg × r)
        </p>
        <p className="mt-2">
          Der Wert <strong>'r'</strong> ist der Reduktions- oder
          Verteilungsfaktor, der den unterschiedlichen Wasseranteil im Körper
          berücksichtigt.
        </p>
      </div>
      <div>
        <h3 className="text-lg font-semibold">
          Was bedeutet der Reduktionsfaktor (r)?
        </h3>
        <p className="mt-2">
          Der Reduktionsfaktor 'r' gibt an, in welchem Anteil des Körpergewichts
          sich der Alkohol verteilt. Da Frauen tendenziell einen höheren
          Körperfett- und einen geringeren Wasseranteil als Männer haben, wird
          der Alkohol auf weniger Flüssigkeit verteilt. Wir verwenden die
          Standardwerte:
        </p>
        <ul className="list-disc list-inside mt-2 pl-4">
          <li>
            <strong>Männer:</strong> r = 0.7
          </li>
          <li>
            <strong>Weiblich:</strong> r = 0.6
          </li>
        </ul>
      </div>
      <div>
        <h3 className="text-lg font-semibold">
          Wie wird der Alkoholabbau im Körper simuliert?
        </h3>
        <p className="mt-2">
          Der Rechner simuliert sowohl die Aufnahme (Resorption) als auch den
          Abbau des Alkohols.
        </p>
        <ul className="list-disc list-inside mt-2 pl-4 space-y-1">
          <li>
            <strong>Aufnahme:</strong> Wir nehmen an, dass der Alkohol eines
            Getränks über einen Zeitraum von <strong>30 Minuten</strong>{" "}
            vollständig ins Blut aufgenommen wird.
          </li>
          <li>
            <strong>Abbau:</strong> Ab 30 Minuten nach Konsumbeginn beginnt der
            Körper mit dem Abbau. Wir rechnen mit einem konstanten stündlichen
            Abbauwert von <strong>0,15 Promille</strong>. Dieser Wert ist ein
            Durchschnitt und kann individuell variieren.
          </li>
        </ul>
      </div>
      <div>
        <h3 className="text-lg font-semibold">Wie genau ist die Berechnung?</h3>
        <p className="mt-2">
          <strong>Wichtiger Hinweis:</strong> Das Ergebnis ist eine{" "}
          <strong>Schätzung</strong>. Die tatsächliche Blutalkoholkonzentration
          kann von vielen weiteren Faktoren abhängen, wie z.B. der Magenfüllung,
          der Tagesform, Medikamenteneinnahme oder der individuellen
          Stoffwechselgeschwindigkeit. Der Rechner dient ausschließlich zur
          Orientierung und ist <strong>rechtlich nicht bindend</strong>.
          Verlassen Sie sich niemals auf einen solchen Rechner, um zu
          entscheiden, ob Sie noch fahrtüchtig sind.
        </p>
      </div>
    </div>
    <Link
      to="/"
      className="mt-6 inline-block px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
    >
      ← Zurück zum Rechner
    </Link>
  </div>
);

// Komponente für den Blog

type BlogPost = {
  slug: string;
  id: string;
  title: string;
  teaser: string;
  content: JSX.Element;
  datePublished: string;
  dateModified: string;
  imageUrl: string;
};

const blogPosts: BlogPost[] = [
  {
    slug: "dry-january-alkoholfreie-alternativen",
    id: "dry-january-alkoholfreie-alternativen",
    title: "Dry January: Alkoholfreie Alternativen, die wirklich schmecken",
    teaser:
      "Ein alkoholfreier Januar kann gut tun. Wir zeigen, welche Drinks, Rituale und Tipps den Verzicht leicht machen.",
    datePublished: "2026-01-06",
    dateModified: "2026-01-06",
    imageUrl: "https://calcalc.de/og-image.png",
    content: (
      <div className="space-y-4">
        <p className="text-sm text-gray-500">Stand: 06.01.2026</p>
        <p>
          Der Dry January ist eine einfache Idee mit spürbarem Effekt: 31 Tage
          ohne Alkohol, um dem Körper eine Pause zu geben, neue Gewohnheiten zu
          testen und den eigenen Konsum besser einzuordnen. Viele berichten von
          besserem Schlaf, mehr Energie und einem klareren Kopf.
        </p>

        <h3 className="text-xl font-semibold">Warum Dry January beliebt ist</h3>
        <ul className="list-disc list-inside pl-4">
          <li>Erholungsphase für Leber und Stoffwechsel</li>
          <li>Bessere Schlafqualität und stabilere Stimmung</li>
          <li>Bewusster Umgang mit Auslösern und Routinen</li>
          <li>Mehr Geschmackserlebnis durch Alternativen</li>
        </ul>

        <h3 className="text-xl font-semibold">
          Alkoholfreie Alternativen, die nicht nach Verzicht schmecken
        </h3>
        <p>
          Der Markt für alkoholfreie Drinks ist groß. Gute Optionen schmecken
          komplex, haben Struktur und passen zu verschiedenen Anlässen.
        </p>
        <ul className="list-disc list-inside pl-4">
          <li>
            <strong>Alkoholfreies Bier:</strong> Von Pils bis IPA ist vieles
            heute erstaunlich vollmundig.
          </li>
          <li>
            <strong>Entalkoholisierter Wein:</strong> Besonders als leichter
            Begleiter zu Essen geeignet.
          </li>
          <li>
            <strong>Alkoholfreie Spirits:</strong> Perfekt für Longdrinks mit
            Bitterkeit, Kräutern oder Citrus.
          </li>
          <li>
            <strong>Mocktails:</strong> Zitrus, Ingwer, Tee oder Shrubs sorgen
            für Tiefe statt süßer Limonade.
          </li>
          <li>
            <strong>Spritzige Klassiker:</strong> Tonic Water, Ginger Ale oder
            Bitter Lemon mit frischen Kräutern.
          </li>
        </ul>

        <h3 className="text-xl font-semibold">Drei einfache Rezepte für zuhause</h3>
        <ul className="list-disc list-inside pl-4">
          <li>
            <strong>Ginger Citrus:</strong> Ginger Ale, Limette, frischer Ingwer,
            Minze, Eis.
          </li>
          <li>
            <strong>Herbal Spritz:</strong> Alkoholfreier Aperitif, Soda,
            Orangenzeste, Rosmarin.
          </li>
          <li>
            <strong>Cold Tea Mule:</strong> Kalter Schwarztee, Limettensaft,
            Ginger Beer, Gurke.
          </li>
        </ul>

        <h3 className="text-xl font-semibold">Tipps für soziale Situationen</h3>
        <p>
          Plane dein Getränk im Voraus, bestelle bewusst und halte eine kurze
          Antwort parat, falls jemand nachfragt. Ein alkoholfreier Drink im
          Glas fühlt sich oft genauso stimmig an wie ein klassischer Cocktail.
        </p>

        <p>
          <strong>Fazit:</strong> Dry January ist kein Verzicht, sondern ein
          Experiment. Mit guten Alternativen bleibt der Genuss, während dein
          Körper eine Pause bekommt.
        </p>
      </div>
    ),
  },  {
    slug: "alkoholabbau-mythen",
    id: "mythen-alkoholabbau",
    title: "Die 5 größten Mythen über den Alkoholabbau",
    teaser:
      "Kaffee, eine kalte Dusche oder ein Konterbier am Morgen? Wir decken auf, was wirklich hilft - und was nicht.",
    datePublished: "2025-07-29",
    dateModified: "2026-01-06",
    imageUrl: "https://calcalc.de/og-image.png",
    content: (
      <div className="space-y-4">
        <p>
          Immer wieder kursieren Ratschläge, wie man nach einer
          feucht-fröhlichen Nacht schnell wieder nüchtern wird. Doch die meisten
          davon sind reine Mythen. Wir schauen uns die gängigsten Irrtümer an
          und erklären, warum am Ende nur eines wirklich hilft: Zeit.
        </p>

        <h3 className="text-xl font-semibold">
          Mythos 1: Kaffee macht nüchtern
        </h3>
        <p>
          Der Klassiker. Ein starker Espresso soll die Sinne schärfen. Das
          stimmt zwar, hat aber nichts mit dem Promillewert zu tun. Koffein ist
          ein Stimulans, das die Müdigkeit bekämpft, aber den Alkohol nicht
          abbaut. Man fühlt sich nur wacher – nicht nüchterner.
        </p>

        <h3 className="text-xl font-semibold">
          Mythos 2: Eine kalte Dusche oder frische Luft hilft
        </h3>
        <p>
          Kurzzeitig fühlt man sich fitter – der Alkoholgehalt im Blut bleibt
          jedoch unverändert. Die Leber arbeitet konstant, unabhängig von
          Wetter, Wasser oder Sauerstoffzufuhr.
        </p>

        <h3 className="text-xl font-semibold">
          Mythos 3: Das „Konterbier“ vertreibt den Kater
        </h3>
        <p>
          Es betäubt kurzfristig – verschlimmert aber langfristig die Symptome.
          Der Körper wird zusätzlich belastet, der Kater nur aufgeschoben.
        </p>

        <h3 className="text-xl font-semibold">
          Mythos 4: Schwitzen in der Sauna spült den Alkohol raus
        </h3>
        <p>
          Weniger als 5 % des Alkohols werden über Schweiß, Atem oder Urin
          ausgeschieden. Der Rest bleibt Aufgabe der Leber – und die lässt sich
          nicht austricksen.
        </p>

        <h3 className="text-xl font-semibold">
          Mythos 5: Eine fettige Mahlzeit nach dem Trinken hilft
        </h3>
        <p>
          Fettiges Essen kann die Aufnahme verlangsamen – nicht aber den Abbau.
          Der Döner auf dem Heimweg hilft eher fürs Wohlbefinden als für den
          Promillewert.
        </p>

        <p>
          <strong>Fazit:</strong> Die Leber baut pro Stunde etwa 0,1 bis 0,15
          Promille ab. Es hilft nur eins: Geduld und viel Wasser.
        </p>
      </div>
    ),
  },
  {
    slug: "widmark-formel",
    id: "widmark-formel",
    title: "Die Widmark-Formel einfach erklärt",
    teaser:
      "Unser Rechner basiert auf ihr – aber wie funktioniert sie genau? Wir erklären die Formel Schritt für Schritt.",
    datePublished: "2025-07-29",
    dateModified: "2026-01-06",
    imageUrl: "https://calcalc.de/og-image.png",
    content: (
      <div className="space-y-4">
        <p>
          Die Widmark-Formel, benannt nach dem schwedischen Chemiker Erik M. P.
          Widmark, ist die wissenschaftliche Grundlage der meisten
          Promillerechner.
        </p>

        <h3 className="text-xl font-semibold">Die Grundformel</h3>
        <p className="bg-gray-100 p-3 rounded font-mono text-center">
          c = A / (m × r)
        </p>
        <ul className="list-disc list-inside pl-4">
          <li>c: Blutalkoholkonzentration in Promille (‰)</li>
          <li>A: Alkoholmenge in Gramm</li>
          <li>m: Körpergewicht in kg</li>
          <li>r: Reduktionsfaktor (Verteilungsvolumen)</li>
        </ul>

        <h3 className="text-xl font-semibold">Beispielrechnung</h3>
        <p>
          500 ml Bier mit 5 Vol-%: 500 × 0,05 × 0,8 = 20 g Alkohol. Bei 70 kg
          und r = 0,7 ergibt das etwa 0,41 ‰.
        </p>

        <h3 className="text-xl font-semibold">Was bedeutet r?</h3>
        <p>
          Männer: r ≈ 0,7 (mehr Körperwasser)
          <br />
          Frauen: r ≈ 0,6 (weniger Körperwasser)
        </p>

        <h3 className="text-xl font-semibold">Der Abbau</h3>
        <p>
          Die Leber baut ca. 0,15‰ pro Stunde ab, beginnend etwa 30–60 Minuten
          nach dem Konsum. Deshalb gilt für später: <br />
          <span className="font-mono">BAK ≈ c - (0,15 × Stunden)</span>
        </p>

        <p className="text-sm text-gray-500">
          Hinweis: Individuelle Faktoren wie Magenfüllung oder Medikamente
          können die BAK beeinflussen. Die Formel liefert nur eine Schätzung.
        </p>
      </div>
    ),
  },
  {
    slug: "promillegrenzen-verkehr",
    id: "promillegrenzen-deutschland",
    title: "Promillegrenzen für Auto, Fahrrad & E-Scooter",
    teaser:
      "Ab wann wird es teuer? Hier findest du die wichtigsten gesetzlichen Grenzen im Überblick.",
    datePublished: "2025-07-29",
    dateModified: "2026-01-06",
    imageUrl: "https://calcalc.de/og-image.png",
    content: (
      <div className="space-y-4">
        <p>
          Wer trinkt, fährt nicht – doch die gesetzlichen Grenzen unterscheiden
          sich je nach Fahrzeug und Situation erheblich.
        </p>

        <h3 className="text-xl font-semibold">Im Auto</h3>
        <ul className="list-disc list-inside pl-4">
          <li>
            <strong>0,0‰:</strong> Gilt für Fahranfänger und unter 21-Jährige.
          </li>
          <li>
            <strong>Ab 0,3‰:</strong> Mit Ausfallerscheinungen → Straftat.
          </li>
          <li>
            <strong>Ab 0,5‰:</strong> Ordnungswidrigkeit (500 €, Punkte,
            Fahrverbot).
          </li>
          <li>
            <strong>Ab 1,1‰:</strong> Absolute Fahruntüchtigkeit → Straftat mit
            Entzug der Fahrerlaubnis.
          </li>
        </ul>

        <h3 className="text-xl font-semibold">Auf dem Fahrrad</h3>
        <ul className="list-disc list-inside pl-4">
          <li>
            <strong>Ab 0,3‰:</strong> Mit Ausfallerscheinungen → strafbar.
          </li>
          <li>
            <strong>Ab 1,6‰:</strong> Straftat + MPU + ggf. Verlust des
            Autoführerscheins.
          </li>
        </ul>

        <h3 className="text-xl font-semibold">E-Scooter</h3>
        <p>
          Für E-Scooter gelten dieselben Promillegrenzen wie für Autofahrer.
          Auch hier drohen bei 0,5‰ Bußgelder und Fahrverbote.
        </p>
      </div>
    ),
  },
];

const Blog = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Startseite",
        item: "https://calcalc.de/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://calcalc.de/blog",
      },
    ],
  };

  if (selectedId) {
    const post = blogPosts.find((p) => p.id === selectedId);
    return (
      <div className="animate-fade-in">
        <title>CalcAlc Blog - Wissen rund um Promille & Alkoholabbau</title>
        <meta
          name="description"
          content="Lesen Sie interessante Artikel über den Alkoholabbau, die Widmark-Formel und Mythen rund um das Thema Nüchternwerden."
        />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbList)}
        </script>
        <h1 className="text-3xl font-bold mb-6 text-gray-800">{post?.title}</h1>
        <div className="bg-white p-6 rounded-lg shadow-md text-gray-700 space-y-4">
          {post?.content}
        </div>
        <button
          onClick={() => setSelectedId(null)}
          className="mt-6 px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg shadow-md hover:bg-purple-700"
        >
          ← Zurück zur Übersicht
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">
        CalcAlc Blog: Wissen rund um Promille & Co.
      </h1>
      <div className="space-y-8">
        {blogPosts.map((post) => (
          <div
            key={post.id}
            className="bg-white p-6 rounded-lg shadow-md text-gray-700"
          >
            <h2 className="text-2xl font-bold text-purple-700 mb-2">
              {post.title}
            </h2>
            <p className="mb-4">{post.teaser}</p>
            <Link
              to={`/blog/${post.slug}`}
              className="font-semibold text-indigo-600 hover:underline"
            >
              Weiterlesen →
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  const postUrl = post ? `https://calcalc.de/blog/${post.slug}` : "";
  const shareText = post ? `${post.title} - CalcAlc` : "";
  const shareUrlEncoded = encodeURIComponent(postUrl);
  const shareTextEncoded = encodeURIComponent(shareText);
  const shareWhatsApp = `https://wa.me/?text=${encodeURIComponent(
    `${shareText} ${postUrl}`
  )}`;
  const shareX = `https://twitter.com/intent/tweet?text=${shareTextEncoded}&url=${shareUrlEncoded}`;
  const shareGmail = `https://mail.google.com/mail/?view=cm&fs=1&su=${shareTextEncoded}&body=${encodeURIComponent(
    `${shareText}\n${postUrl}`
  )}`;
  const blogPosting = post
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.teaser,
        image: post.imageUrl,
        datePublished: post.datePublished,
        dateModified: post.dateModified,
        author: {
          "@type": "Organization",
          name: "CalcAlc",
        },
        mainEntityOfPage: postUrl,
      }
    : null;
  const breadcrumbList = post
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Startseite",
            item: "https://calcalc.de/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: "https://calcalc.de/blog",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: post.title,
            item: `https://calcalc.de/blog/${post.slug}`,
          },
        ],
      }
    : null;

  if (!post) {
    return (
      <div className="p-4 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-red-600">
          Beitrag nicht gefunden
        </h1>
        <Link to="/blog" className="text-indigo-600 hover:underline block mt-4">
          Zurück zum Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white rounded-lg shadow-md animate-fade-in">
      <title>{`${post.title} - CalcAlc Blog`}</title>
      <meta name="description" content={post.teaser} />
      <link rel="canonical" href={postUrl} />
      <meta property="og:title" content={post.title} />
      <meta property="og:description" content={post.teaser} />
      <meta property="og:site_name" content="CalcAlc" />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={postUrl} />
      <meta property="og:image" content={post.imageUrl} />
      <meta property="article:published_time" content={post.datePublished} />
      <meta property="article:modified_time" content={post.dateModified} />
      <link
        rel="alternate"
        type="application/rss+xml"
        title="CalcAlc Blog RSS"
        href="/feed.xml"
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={post.title} />
      <meta name="twitter:description" content={post.teaser} />
      <meta name="twitter:image" content={post.imageUrl} />
      {breadcrumbList && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbList)}
        </script>
      )}
      {blogPosting && (
        <script type="application/ld+json">
          {JSON.stringify({
            ...blogPosting,
            publisher: {
              "@type": "Organization",
              name: "CalcAlc",
              logo: {
                "@type": "ImageObject",
                url: "https://calcalc.de/logo.png",
              },
            },
          })}
        </script>
      )}
      <Link
        to="/blog"
        className="inline-flex items-center text-purple-700 hover:text-purple-800 text-lg font-semibold mb-3"
        aria-label="Zurück zur Blog-Übersicht"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 mr-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Zurück zum Blog
      </Link>
      <h1 className="text-3xl font-bold text-gray-800 mb-4">{post.title}</h1>
      <div className="text-gray-700 space-y-4 whitespace-pre-line">
        {post.content}
      </div>
      <div className="mt-6">
        <p className="text-sm font-semibold text-gray-700 mb-2">Teilen</p>
        <div className="flex flex-wrap gap-2">
          <a
            href={shareWhatsApp}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-green-50 text-green-700 border border-green-200 shadow-sm hover:bg-green-100"
            aria-label="Auf WhatsApp teilen"
            title="WhatsApp"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fill="#25D366"
                d="M16 3C9.4 3 4 8.4 4 15c0 2.2.6 4.3 1.8 6.2L4 29l8-1.7c1.8 1 3.8 1.6 6 1.6 6.6 0 12-5.4 12-12S22.6 3 16 3z"
              />
              <path
                fill="#fff"
                d="M22.4 19.6c-.3-.2-1.9-.9-2.2-1s-.5-.2-.8.2-.9 1-1.1 1.2-.4.2-.7 0c-.3-.2-1.4-.5-2.7-1.6-1-.9-1.6-2-1.8-2.3s0-.5.2-.7c.2-.2.3-.4.5-.6.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6 0-.2-.8-1.9-1.1-2.6-.3-.7-.6-.6-.8-.6h-.6c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.4 1.4 3.6c.2.2 2.4 3.6 5.8 5 3.4 1.4 3.4.9 4 .8.6-.1 1.9-.8 2.2-1.5.3-.7.3-1.3.2-1.5-.1-.1-.3-.2-.6-.4z"
              />
            </svg>
          </a>
          <a
            href={shareX}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-900 text-white shadow-sm hover:bg-gray-800"
            aria-label="Auf X teilen"
            title="X"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M18.3 2H21l-6.6 7.5L22 22h-6.2l-4.8-6.1L5.7 22H3l7.1-8.1L2 2h6.3l4.3 5.5L18.3 2zm-1.1 18h1.7L8.8 4H7.1l10.1 16z"
              />
            </svg>
          </a>
          <a
            href={shareGmail}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white text-gray-700 border border-gray-200 shadow-sm hover:bg-gray-50"
            aria-label="Mit Gmail teilen"
            title="Gmail"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fill="#EA4335"
                d="M12 12.7L3.6 6.3C4.2 5.5 5.1 5 6.1 5h11.8c1 0 1.9.5 2.5 1.3L12 12.7z"
              />
              <path
                fill="#34A853"
                d="M3 7.4v9.2c0 1.3 1.1 2.4 2.4 2.4h4.2V13L3 7.4z"
              />
              <path
                fill="#FBBC05"
                d="M21 7.4L14.4 13v6h4.2c1.3 0 2.4-1.1 2.4-2.4V7.4z"
              />
              <path
                fill="#4285F4"
                d="M9.6 13v6h4.8v-6l-2.4 1.8L9.6 13z"
              />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};
// Hauptkomponente für den Rechner
const Calculator = ({ setView }: { setView: (view: View) => void }) => {
  // ... Logik wie gehabt ...
  const [weight, setWeight] = useState<number>(70);
  const [gender, setGender] = useState<"male" | "female">("male");
  const [drinks, setDrinks] = useState<Drink[]>([
    {
      type: "Bier",
      volume: 500,
      alcohol: 5,
      time: getCurrentTimeRoundedTo30Min(),
    },
  ]);
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [calculated, setCalculated] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const createShareableURL = () => {
    const payload = {
      weight,
      gender,
      drinks,
    };
    const encoded = btoa(encodeURIComponent(JSON.stringify(payload)));
    return `${window.location.origin}/?data=${encoded}`;
  };
  const [showQRCode, setShowQRCode] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("data");
    if (encoded) {
      try {
        const decoded = decodeURIComponent(atob(encoded));
        const parsed = JSON.parse(decoded);
        if (parsed.weight && parsed.gender && parsed.drinks) {
          setWeight(parsed.weight);
          setGender(parsed.gender);
          setDrinks(parsed.drinks);
          setTimeout(() => calculatePromille(), 100); // leicht verzögert berechnen
        }
      } catch (e) {
        console.warn("Ungültige Daten in der URL:", e);
      }
    }
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 600);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const addDrink = () => {
    setDrinks([
      ...drinks,
      {
        type: "Bier",
        volume: 500,
        alcohol: 5,
        time: getCurrentTimeRoundedTo30Min(),
      },
    ]);
    setCalculated(false);
  };

  const removeDrink = (index: number) => {
    const updated = drinks.filter((_, i) => i !== index);
    setDrinks(updated);
    setCalculated(false);
  };

  const handleDrinkChange = <K extends keyof Drink>(
    index: number,
    field: K,
    value: Drink[K]
  ) => {
    const updated = drinks.map((drink, i) =>
      i === index ? { ...drink, [field]: value } : drink
    );
    setDrinks(updated);
    setCalculated(false);
  };

  const calculatePromille = () => {
    if (drinks.some((d) => !d.type || d.volume === "" || d.volume === 0)) {
      alert("Bitte füllen Sie alle Felder für die Getränke aus.");
      return;
    }

    const r = gender === "male" ? 0.7 : 0.6;
    const baseDate = new Date();
    baseDate.setSeconds(0);
    baseDate.setMilliseconds(0);

    const parsedDrinks = drinks.map((d) => {
      const parsed = parse(d.time, "HH:mm", baseDate);
      if (parsed.getHours() < 7) parsed.setDate(parsed.getDate() + 1);
      return {
        ...d,
        parsedTime: parsed,
        alcoholG: ((d.volume === "" ? 0 : d.volume) * d.alcohol * 0.8) / 100,
      };
    });

    const timestamps = parsedDrinks.map((d) => d.parsedTime.getTime()).sort();
    if (timestamps.length === 0) return;

    const startTime = new Date(timestamps[0]);
    const endTime = addMinutes(
      new Date(timestamps[timestamps.length - 1]),
      6 * 60
    );

    const timeline: Date[] = [];
    for (let t = new Date(startTime); t <= endTime; t = addMinutes(t, 30)) {
      timeline.push(new Date(t));
    }

    let currentPromille = 0;
    const promilleSeries: number[] = [];

    timeline.forEach((time, index) => {
      const current = time.getTime();
      const prev = index > 0 ? timeline[index - 1].getTime() : current;

      // Alkoholaufnahme in diesem Zeitintervall
      let addedG = 0;

      parsedDrinks.forEach((drink) => {
        const start = drink.parsedTime.getTime();
        const end = addMinutes(drink.parsedTime, 30).getTime();

        // Berechne Überlappung mit dem aktuellen Intervall
        const overlapStart = Math.max(prev, start);
        const overlapEnd = Math.min(current, end);
        const durationMin = (overlapEnd - overlapStart) / 60000;

        if (durationMin > 0) {
          const fraction = durationMin / 30;
          addedG += drink.alcoholG * fraction;
        }
      });

      // Promilleabbau (0.15‰ pro Stunde → 0.075‰ pro 30 Min)
      if (index > 0) {
        currentPromille = Math.max(0, currentPromille - 0.075);
      }

      // Promilleaufnahme
      const deltaPromille = addedG / (weight * r);
      currentPromille += deltaPromille;

      promilleSeries.push(parseFloat(currentPromille.toFixed(3)));
    });

    const chartData: ChartPoint[] = timeline.map((t, i) => ({
      time: format(t, "HH:mm"),
      promille: promilleSeries[i],
      numericTime: t.getTime(),
    }));

    setChartData(chartData);
    setCalculated(true);
  };

  const getSoberTime = (): string => {
    // ... getSoberTime Logik bleibt unverändert ...
    if (chartData.length === 0) return "";
    const lastAbove = [...chartData]
      .map((p, i) => (p.promille > 0.01 ? i : -1))
      .filter((i) => i !== -1)
      .pop();

    if (lastAbove === undefined) return chartData[0]?.time || "";
    const soberIndex = lastAbove + 1;
    if (soberIndex >= chartData.length) return "Leg dich erstmal hin 😴";

    return chartData[soberIndex].time;
  };

  return (
    <div className="animate-fade-in">
      <title>CalcAlc - Promillerechner mit grafischem Zeitverlauf</title>
      <meta
        name="description"
        content="Wie verändert sich dein Promillewert? Der kostenlose Promillerechner von CalcAlc zeigt dir den Alkoholabbau im Zeitverlauf – grafisch, einfach & intuitiv."
      />
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        Promillerechner 🍻
      </h1>
      {/* Rest des Rechner-JSX bleibt unverändert */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <label className="block font-semibold text-gray-700">
            Körpergewicht (kg)
          </label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(parseFloat(e.target.value))}
            className="mt-2 p-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <label className="block font-semibold text-gray-700">
            Geschlecht
          </label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value as "male" | "female")}
            className="mt-2 p-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 bg-white"
          >
            <option value="male">Männlich</option>
            <option value="female">Weiblich</option>
          </select>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Alkoholkonsum</h2>
        <div className="relative flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-gray-400 cursor-pointer"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            onClick={() => setShowInfo(!showInfo)}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          {showInfo && (
            <div className="absolute bottom-full left-1/2 z-20 mb-3 w-72 -translate-x-1/2 transform rounded-lg bg-gray-800 p-3 text-sm text-white">
              <p className="font-sans">
                Ein <strong>Promillerechner</strong> hilft dir dabei, deinen
                geschätzten Blutalkoholwert basierend auf deinem Körpergewicht,
                Geschlecht und den konsumierten Getränken zu berechnen. CalcAlc
                geht einen Schritt weiter: Du siehst den
                <strong> Promilleverlauf im Zeitverlauf</strong> und erfährst,
                wann du voraussichtlich wieder nüchtern bist.
              </p>
              <p className="mt-2 text-xs text-gray-300">
                Die Ergebnisse sind nur Schätzungen und nicht rechtsverbindlich.
              </p>
              <div className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-gray-800"></div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4 mb-6">
        {drinks.map((drink, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-lg shadow-sm flex flex-wrap gap-4 items-end"
          >
            <div className="flex-grow min-w-[120px]">
              <label className="block text-sm font-medium text-gray-600">
                Art
              </label>
              <select
                value={drink.type}
                onChange={(e) => {
                  const selectedType = e.target.value;
                  const matching = drinkOptions.find(
                    (d) => d.type === selectedType
                  );

                  // Nur fortfahren, wenn eine gültige Option (nicht der Platzhalter) gewählt wurde
                  if (matching) {
                    // Bestimme den neuen Alkoholwert basierend auf der Auswahl
                    const newAlcohol =
                      selectedType === "Individuell" ? 0 : matching.alcohol;

                    // Erstelle das komplett aktualisierte Getränke-Objekt
                    const updatedDrink = {
                      ...drink,
                      type: selectedType,
                      alcohol: newAlcohol,
                    };

                    // Erstelle die neue Liste und aktualisiere den State in einem Schritt
                    const updatedDrinks = drinks.map((d, i) =>
                      i === index ? updatedDrink : d
                    );
                    setDrinks(updatedDrinks);
                    setCalculated(false);
                  }
                }}
                className="mt-1 p-2 w-full border border-gray-300 rounded-md bg-white"
              >
                <option value="">- Getränk -</option>
                {drinkOptions.map((opt) => (
                  <option key={opt.type} value={opt.type}>
                    {opt.type}
                  </option>
                ))}
              </select>
            </div>
            {/* NEU: Bedingtes Eingabefeld für den Alkoholgehalt */}
            {drink.type === "Individuell" && (
              <div className="flex-grow min-w-[90px] animate-fade-in">
                <label className="block text-sm font-medium text-gray-600">
                  Alkohol (Vol.-%)
                </label>
                <input
                  type="number"
                  value={drink.alcohol}
                  onChange={(e) =>
                    handleDrinkChange(index, "alcohol", Number(e.target.value))
                  }
                  placeholder="z.B. 15"
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md"
                />
              </div>
            )}
            <div className="flex-grow min-w-[90px]">
              <label className="block text-sm font-medium text-gray-600">
                Menge (ml)
              </label>
              <input
                type="number"
                value={drink.volume}
                onChange={(e) =>
                  handleDrinkChange(
                    index,
                    "volume",
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                className="mt-1 p-2 w-full border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex-grow min-w-[100px]">
              <label className="block text-sm font-medium text-gray-600">
                Uhrzeit
              </label>
              <select
                value={drink.time}
                onChange={(e) =>
                  handleDrinkChange(index, "time", e.target.value)
                }
                className="mt-1 p-2 w-full border border-gray-300 rounded-md bg-white"
              >
                {generateTimeOptions().map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => removeDrink(index)}
              className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-colors"
              aria-label="Getränk entfernen"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <button
          onClick={addDrink}
          className="w-full sm:w-auto px-6 py-3 bg-purple-600 text-white font-bold rounded-lg shadow-md hover:bg-purple-700 transition-all transform hover:scale-105"
        >
          ➕ Getränk hinzufügen
        </button>
        <button
          onClick={calculatePromille}
          className="w-full sm:w-auto px-6 py-3 bg-indigo-500 text-white font-bold rounded-lg shadow-md hover:bg-indigo-600 transition-all transform hover:scale-105"
        >
          📈 Promilleverlauf berechnen
        </button>
      </div>
      {calculated && chartData.length > 0 && (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-lg animate-fade-in">
          <div className="text-center mb-4">
            <p className="text-lg text-gray-800">
              Voraussichtlich wieder nüchtern um:
            </p>
            <strong className="text-2xl font-bold text-indigo-600">
              {getSoberTime()}
            </strong>
          </div>
          <h2 className="text-xl font-bold mb-4 text-center text-gray-800">
            Promille-Verlauf
          </h2>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  type="number"
                  dataKey="numericTime"
                  domain={["auto", "auto"]}
                  tickFormatter={(tick) => {
                    const d = new Date(tick);
                    return format(d, "HH:mm");
                  }}
                  ticks={chartData
                    .filter((_, i) => i % (isMobile ? 2 : 1) === 0)
                    .map((d) => d.numericTime)}
                  scale="time"
                  dy={10}
                />
                <YAxis
                  domain={[
                    0,
                    (dataMax: number) =>
                      dataMax <= 3 ? 3 : Math.ceil(dataMax * 2) / 2,
                  ]}
                  ticks={(() => {
                    const maxPromille = Math.max(
                      ...chartData.map((d) => d.promille),
                      0
                    );
                    const upperLimit =
                      maxPromille <= 3 ? 3 : Math.ceil(maxPromille * 2) / 2;
                    const tickArray: number[] = [];
                    for (let i = 0; i <= upperLimit; i += 0.5) {
                      tickArray.push(parseFloat(i.toFixed(1)));
                    }
                    return tickArray;
                  })()}
                  tickFormatter={(v) => v.toFixed(1)}
                  dx={-10}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const time =
                        typeof label === "number" ? new Date(label) : null;
                      return (
                        <div className="bg-white p-2 border border-gray-300 rounded shadow text-sm text-gray-800">
                          {time && (
                            <div>
                              <strong>Uhrzeit:</strong> {format(time, "HH:mm")}
                            </div>
                          )}
                          <div>
                            <strong>Promille:</strong>{" "}
                            {payload[0].value.toFixed(2)} ‰
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="promille"
                  name="Promille (‰)"
                  stroke="#7c3aed"
                  strokeWidth={3}
                  dot={false}
                  activeDot={{ r: 6 }}
                />
                {(() => {
                  const now = new Date();
                  now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0);
                  const nowTime = now.getTime();
                  const chartStart = chartData[0]?.numericTime ?? 0;
                  const chartEnd =
                    chartData[chartData.length - 1]?.numericTime ?? 0;
                  const showLine = nowTime >= chartStart && nowTime <= chartEnd;
                  return showLine ? (
                    <ReferenceLine
                      x={nowTime}
                      stroke="#ef4444"
                      strokeDasharray="4 4"
                      strokeWidth={2}
                      label={{
                        value: "Jetzt",
                        position: "insideTopRight",
                        fill: "#ef4444",
                        fontSize: 14,
                        fontWeight: "bold",
                      }}
                    />
                  ) : null;
                })()}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {calculated && chartData.length > 0 && (
        <div className="mt-6 text-center animate-fade-in">
          <button
            onClick={() => setShowQRCode(!showQRCode)}
            className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg shadow-sm hover:bg-gray-300 transition-colors duration-200"
          >
            🔗 Promillekurve teilen
          </button>
          {showQRCode && (
            <div className="mt-4 flex flex-col items-center text-center text-sm text-gray-700">
              <QRCodeCanvas value={createShareableURL()} size={128} />
              <p className="mt-3">
                Teile deinen Promilleverlauf mit diesem Link:
              </p>
              <code className="bg-gray-100 text-indigo-600 px-3 py-1 rounded text-xs truncate max-w-xs">
                {window.location.origin + "/?data=…"}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(createShareableURL());
                  alert("Link kopiert!");
                }}
                className="mt-2 px-4 py-2 bg-gray-100 text-indigo-600 font-medium rounded-md shadow-sm hover:bg-gray-200 transition-colors text-sm"
              >
                📋 Link kopieren
              </button>
            </div>
          )}
        </div>
      )}
      {/* ===== START: SEO-Textblock ===== */}
      <div className="mt-16 p-6 bg-white rounded-lg shadow-md text-gray-700">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          So funktioniert der Promillerechner mit Zeitverlauf
        </h2>
        <div className="space-y-4">
          <p>
            Der CalcAlc Promillerechner hilft Ihnen dabei, eine fundierte{" "}
            <strong>Schätzung</strong> Ihres Alkoholspiegels zu erhalten. Doch
            unser Tool kann mehr als nur den aktuellen Wert anzeigen: Es
            visualisiert den kompletten{" "}
            <strong>Alkoholabbau im Zeitverlauf</strong>. Geben Sie einfach Ihr
            Körpergewicht, Geschlecht und die konsumierten Getränke mit der
            jeweiligen Uhrzeit ein. Unsere interaktive{" "}
            <strong>Promillekurve</strong> zeigt Ihnen dann grafisch, wie sich
            Ihr Promillewert über die nächsten Stunden entwickelt und wann Sie
            voraussichtlich wieder nüchtern sind.
          </p>
          <p>
            Die Berechnung basiert auf der wissenschaftlich anerkannten{" "}
            <strong>Widmark-Formel</strong>, welche die Verteilung des Alkohols
            im Körper berücksichtigt. Wir simulieren die Alkoholaufnahme
            (Resorption) sowie den konstanten Abbau durch die Leber mit einem
            angenommenen Wert von 0,15 Promille pro Stunde. So können Sie den
            Verlauf Ihres Alkoholspiegels einfach und intuitiv{" "}
            <strong>online berechnen</strong> und ein besseres Verständnis für
            die Wirkung von Alkohol auf Ihren Körper entwickeln.
          </p>
          <p>
            Mehr Hintergründe finden Sie im Blog:{" "}
            <Link to="/blog/widmark-formel" className="text-indigo-600 hover:underline">
              Widmark-Formel
            </Link>
            ,{" "}
            <Link to="/blog/alkoholabbau-mythen" className="text-indigo-600 hover:underline">
              Mythen zum Alkoholabbau
            </Link>{" "}
            und{" "}
            <Link to="/blog/promillegrenzen-verkehr" className="text-indigo-600 hover:underline">
              Promillegrenzen im Verkehr
            </Link>
            .
          </p>
          <p className="text-sm text-gray-500 pt-4 border-t border-gray-200">
            <strong>Wichtiger Hinweis:</strong> Alle berechneten Werte sind
            Schätzungen und dienen ausschließlich der Orientierung. Die
            tatsächliche Blutalkoholkonzentration kann durch viele Faktoren wie
            Mageninhalt, Tagesform oder Medikamente beeinflusst werden. Dieses
            Tool ersetzt keinen offiziellen Alkoholtest und sollte niemals als
            Grundlage für die Entscheidung zur Fahrtüchtigkeit dienen.
          </p>
        </div>
      </div>
      {/* ===== ENDE: SEO-Textblock ===== */}
    </div>
  );
};

// --- Haupt-App-Komponente ---
export default function App() {
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CalcAlc",
    url: "https://calcalc.de/",
    logo: "https://calcalc.de/logo.png",
  };
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CalcAlc",
    url: "https://calcalc.de/",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://calcalc.de/?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; background-color: #f3f4f6; }
        .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <Router>
        <div className="p-4 sm:p-6 max-w-4xl mx-auto text-gray-800">
          <Header />
          <main>
            <Routes>
              <Route path="/" element={<Calculator setView={() => {}} />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/faq" element={<FAQ setView={() => {}} />} />
              <Route
                path="/impressum"
                element={<Impressum setView={() => {}} />}
              />
              <Route
                path="/privacy"
                element={<PrivacyPolicy setView={() => {}} />}
              />
            </Routes>
          </main>
          <footer className="mt-12 pt-6 border-t border-gray-300 text-center text-sm text-gray-500">
            <p>
              &copy; {new Date().getFullYear()} CalcAlc. Alle Rechte
              vorbehalten.
            </p>
            <p className="mt-2">
              <a href="/impressum" className="underline hover:text-purple-600">
                Impressum
              </a>
              <span className="mx-2">|</span>
              <a href="/privacy" className="underline hover:text-purple-600">
                Datenschutzerklärung
              </a>
              <span className="mx-2">|</span>
              <a href="/faq" className="underline hover:text-purple-600">
                FAQ
              </a>
            </p>
            <p className="mt-4 text-xs">
              Haftungsausschluss: Dieser Rechner dient nur zu
              Informationszwecken. Die Ergebnisse sind Schätzungen und nicht
              rechtsverbindlich.
            </p>
          </footer>
        </div>
      </Router>
    </>
  );
}



